import React, { useEffect, useState } from 'react';
import {
  Device,
  EnergyRectifier,
  useEnergyRectifierDetailQuery,
  useEnergyRectifierDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import { DeviceInformation } from '../shared-ui/device-info';
import { PanelFrame, ViewMode } from '../shared-ui/panel-frame';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';

interface EnergyRectifierProps {
  id: string;
}

export const EnergyRectifierComponent: React.FC<EnergyRectifierProps> = ({
  id,
}) => {
  const [device, setDevice] = useState<Partial<EnergyRectifier>>();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  const { data } = useEnergyRectifierDetailQuery({
    variables: { id },
  });

  useEnergyRectifierDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.energyRectifierById) {
      setDevice(data.energyRectifierById as Partial<EnergyRectifier>);
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const info: DeviceInformation[] = [
        {
          'Device Identification': {
            'Device Name': device.name ?? 'Unnamed Energy Rectifier',
            'Device ID': device.deviceId ?? 'Unidentified',
            'Device Type': device.typeDisplayName ?? 'Unknown',
            'Last Updated': moment(device.timestamp).fromNow(),
          },
          'Performance Metrics': {
            'Output Voltage': formatDeviceMetric({
              value: device.outputVoltage?.value ?? undefined,
              units: device.outputVoltage?.units ?? undefined,
            }),
            'Rectifier Efficiency': formatDeviceMetric({
              value: device.rectifierEfficiency?.value ?? undefined,
              units: device.rectifierEfficiency?.units ?? undefined,
            }),
          },
          'System Status': {
            'Power State': device.powerState?.value ? 'On' : 'Off',
            'Is Powered On': device.isPoweredOn ? 'Yes' : 'No',
            'Is Configured': device.isConfigured ? 'Yes' : 'No',
            'Is Controlled': device.isControlled ? 'Yes' : 'No',
            'Has Pending Updates': device.hasPendingUpdates ? 'Yes' : 'No',
          },
          'System Integration': {
            Property: device.property?.title ?? 'Not assigned',
            Unit: device.unit?.name ?? 'Not assigned',
            Panel: device.panel?.displayName ?? 'Not assigned',
            Source: device.source?.name ?? 'Not assigned',
          },
        },
      ];
      setDeviceInfo(info);
    }
  }, [device]);

  if (!device) {
    return <DeviceLoadingSkeleton size="large" />;
  }

  return (
    <PanelFrame
      title="Energy Rectifier"
      iconType="mdi:current-dc"
      device={device as Partial<Device>}
      infoEntries={deviceInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Output Voltage
            </Typography>
            <Typography variant="h6" align="center">
              {formatDeviceMetric({
                value: device.outputVoltage?.value ?? undefined,
                units: device.outputVoltage?.units ?? undefined,
              })}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Efficiency
            </Typography>
            <Typography variant="h6" align="center">
              {formatDeviceMetric({
                value: device.rectifierEfficiency?.value ?? undefined,
                units: device.rectifierEfficiency?.units ?? undefined,
              })}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </PanelFrame>
  );
};
